import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { DateTime } from 'luxon'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { RoomState } from '../models/RoomState'
import { fetchRoomStatesInRange } from '../firestore/RoomState'
import { Cubit } from './core'

export type CalendarMode = 'rooms' | 'users'

export interface CalendarCell {
  dayIdx: number // 0..6
  hourIdx: number // 0..23
  rooms: number
  users: number
}

export class CalendarCubit extends Cubit {
  repository: FirebaseRepository

  @observable selectedWeekStart: DateTime
  @observable mode: CalendarMode = 'rooms'
  @observable loading = false
  @observable private rooms: RoomState[] = []

  constructor(repository: FirebaseRepository) {
    super()
    makeObservable(this)
    this.repository = repository
    this.selectedWeekStart = DateTime.now().startOf('week')
  }

  initialize(): void {
    this.fetch()
  }

  @computed
  get weekLabel(): string {
    const end = this.selectedWeekStart.plus({ days: 6 })
    const startStr = this.selectedWeekStart.toLocaleString({
      month: 'short',
      day: 'numeric',
    })
    const endStr = end.toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    return `${startStr} – ${endStr}`
  }

  @computed
  get cells(): CalendarCell[][] {
    const grid: CalendarCell[][] = Array.from({ length: 7 }, (_, dayIdx) =>
      Array.from({ length: 24 }, (_, hourIdx) => ({
        dayIdx,
        hourIdx,
        rooms: 0,
        users: 0,
      }))
    )

    const today = DateTime.now().startOf('day')
    const weekStart = this.selectedWeekStart

    for (const room of this.rooms) {
      const ts = this.timestampForRoom(room, today)
      if (!ts) continue

      const t = DateTime.fromJSDate(ts)
      const dayIdx = Math.floor(t.diff(weekStart, 'days').days)
      const hourIdx = t.hour

      if (dayIdx < 0 || dayIdx > 6) continue

      grid[dayIdx][hourIdx].rooms += 1
      grid[dayIdx][hourIdx].users += room.data.userIds.length
    }

    return grid
  }

  @computed
  get totalRooms(): number {
    return this.rooms.length
  }

  @computed
  get totalUsers(): number {
    return this.rooms.reduce((sum, r) => sum + r.data.userIds.length, 0)
  }

  @computed
  get maxValue(): number {
    let max = 0
    for (const day of this.cells) {
      for (const cell of day) {
        const v = this.mode === 'rooms' ? cell.rooms : cell.users
        if (v > max) max = v
      }
    }
    return max
  }

  @action
  setMode(mode: CalendarMode) {
    this.mode = mode
  }

  @action
  async navigateWeek(offset: number) {
    this.selectedWeekStart = this.selectedWeekStart.plus({ weeks: offset })
    await this.fetch()
  }

  private timestampForRoom(
    room: RoomState,
    today: DateTime
  ): Date | null | undefined {
    const roomStartedAt = room.data.roomStartedAt
    if (roomStartedAt) {
      const roomDay = DateTime.fromJSDate(roomStartedAt).startOf('day')
      if (roomDay < today) return roomStartedAt
    }
    return room.data.scheduledAt
  }

  @action
  private async fetch() {
    this.loading = true
    try {
      const weekStart = this.selectedWeekStart.startOf('day')
      const weekEnd = weekStart.plus({ days: 7 })
      const today = DateTime.now().startOf('day')

      const queries: Promise<RoomState[]>[] = []

      if (weekStart < today) {
        queries.push(
          fetchRoomStatesInRange(this.repository, {
            start: weekStart.toJSDate(),
            end: (today < weekEnd ? today : weekEnd).toJSDate(),
            field: 'roomStartedAt',
          })
        )
      }

      if (weekEnd > today) {
        queries.push(
          fetchRoomStatesInRange(this.repository, {
            start: (today > weekStart ? today : weekStart).toJSDate(),
            end: weekEnd.toJSDate(),
            field: 'scheduledAt',
          })
        )
      }

      const results = await Promise.all(queries)
      runInAction(() => {
        this.rooms = results.flat()
        this.loading = false
      })
    } catch {
      runInAction(() => {
        this.loading = false
      })
    }
  }
}
